import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";

const featured = {
  img: "/photos/IMG_9507.jpeg",
  alt: "Coach Abrie supporting a laughing toddler in a pink swimsuit during a trial lesson",
  tag: "Perk 01 · Free",
  title: "Free Trial Lesson",
  text: "Book online in under a minute, meet Coach Abrie, our Program Director, and her caring instructors — then watch your child splash into a completely free trial lesson.",
  id: "perk-card-free-trial",
  imgClass: "h-72 sm:h-96 w-full object-cover object-[50%_18%]",
};

const side = [
  {
    img: "/photos/IMG_9500.jpeg",
    alt: "Young girl floating confidently on her back with a big smile",
    tag: "Perk 02 · 15% Resident Discount",
    title: "Discounted 6-Week Mini Session",
    text: "Now enrolling for fall: residents save 15% on our 6-week mini session — up to $600 a year versus public pricing.",
    id: "perk-card-discount",
    imgClass: "h-48 w-full sm:h-full sm:w-44 shrink-0 object-cover object-[50%_28%]",
  },
  {
    img: "/photos/IMG_9518.jpeg",
    alt: "Lesson in progress at the Four Seasons community pool",
    tag: "Perk 03 · Zero Commute",
    title: "Lessons at Your Own Pool",
    text: "Every lesson happens right here at your Four Seasons community pool — 3003 Woodland Ave. No packing bags, no driving across town.",
    id: "perk-card-onsite",
    imgClass: "h-48 w-full sm:h-full sm:w-44 shrink-0 object-cover object-[50%_55%]",
  },
];

const Tag = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#c97049]">{children}</p>
);

export default function Perks({ onEnroll }) {
  return (
    <section id="perks" data-testid="perks-section" className="scroll-mt-24 bg-[#f8f3e9]">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#c97049]">
            <Sparkles className="h-4 w-4" />
            Your on-property advantages
          </p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#00334a]">
            Three perks only Four Seasons residents get
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55 }}
            data-testid={featured.id}
            className="card-lift lg:row-span-2 flex h-full flex-col overflow-hidden rounded-3xl bg-white border border-[#00334a]/10 shadow-sm ring-2 ring-[#c97049]/40"
          >
            <img src={featured.img} alt={featured.alt} className={featured.imgClass} loading="lazy" />
            <div className="flex flex-1 flex-col p-6 sm:p-8">
              <Tag>{featured.tag}</Tag>
              <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-[#00334a]">{featured.title}</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#00334a]/70">{featured.text}</p>
            </div>
          </motion.article>
          {side.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: 0.12 + i * 0.12 }}
              data-testid={p.id}
              className="card-lift flex flex-col sm:flex-row overflow-hidden rounded-3xl bg-white border border-[#00334a]/10 shadow-sm"
            >
              <img src={p.img} alt={p.alt} className={p.imgClass} loading="lazy" />
              <div className="flex flex-1 flex-col justify-center p-6">
                <Tag>{p.tag}</Tag>
                <h3 className="mt-2 font-serif text-xl sm:text-2xl font-semibold text-[#00334a]">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#00334a]/70">{p.text}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.2 }}
          className="mt-14 text-center"
        >
          <button
            type="button"
            data-testid="midpage-enroll-button"
            onClick={onEnroll}
            className="group inline-flex items-center gap-3 rounded-full bg-[#00334a] px-8 py-4 text-base sm:text-lg font-semibold text-[#f8f3e9] shadow-md hover:bg-[#002233] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
          >
            Enroll in the 6-Week Mini Session
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <p className="mt-4 text-xs sm:text-sm text-[#52656d]">
            Resident rate applied · Fall session begins Monday, October 12
          </p>
        </motion.div>
      </div>
    </section>
  );
}
